/**
 * TicketedEventCollection Entity Schemas
 *
 * A collection groups multiple events under a title + description and is served
 * only at /e/[school-slug]/[collection-publicId] — the CUID `publicId` is the
 * unguessable slug (obscurity + noindex, NOT access control — plan KTD6). It is
 * not enumerable from any public browse surface (R15–R18).
 */

import { z } from 'zod'
import { TicketedEventAudienceSetSchema } from './ticketed-event-audience'
import { TicketedEventPublicSchema, TicketedEventSchoolBrandingSchema } from './ticketed-event'

/**
 * Organizer branding embedded on the admin collection DTO. `null` when the
 * collection has no organizer (the card omits the organizer row entirely).
 */
export const TicketedEventCollectionOrganizerSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().nullable()
  })
  .nullable()

/**
 * Organizer branding on the PUBLIC collection page — no `id` is exposed (the
 * public surface never needs the organizer's handle). `null` → row omitted.
 */
export const TicketedEventCollectionPublicOrganizerSchema = z
  .object({
    name: z.string(),
    logoUrl: z.string().nullable()
  })
  .nullable()

/**
 * Admin-facing collection DTO. `id` is the CUID `publicId`. `audiences` are the
 * metadata-only role-visibility hints (no enforcement this release).
 */
export const TicketedEventCollectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  organizer: TicketedEventCollectionOrganizerSchema,
  audiences: TicketedEventAudienceSetSchema,
  eventCount: z.number().int(),
  // How many of this collection's events would appear on the public events page
  // if the collection were deleted — published AND not yet ended. Distinct from
  // `eventCount`, which counts drafts and past events that never become
  // publicly visible. The delete confirmation names THIS number (R13). Optional
  // so older cached payloads without the field stay valid.
  publiclyVisibleEventCount: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type TicketedEventCollection = z.infer<typeof TicketedEventCollectionSchema>

/**
 * Public collection page payload, reachable ONLY by direct `publicId` link.
 * Carries school branding + the collection's PUBLISHED events (draft/unpublished
 * events assigned to the collection are filtered out server-side so they cannot
 * leak via the public link).
 */
export const TicketedEventCollectionPublicSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  organizer: TicketedEventCollectionPublicOrganizerSchema,
  school: TicketedEventSchoolBrandingSchema,
  // This collection's OWN hero — the soonest eligible event among its members
  // (U6/R28). A collection page never inherits the school's featured
  // designation, which governs the public events page only. Null when the
  // collection holds no eligible event. Disjoint from `events`, exactly as on
  // the hub payload. Optional so older cached payloads without the field stay
  // valid, matching how every other field was added to this schema.
  featured: TicketedEventPublicSchema.nullable().optional(),
  events: z.array(TicketedEventPublicSchema)
})
export type TicketedEventCollectionPublic = z.infer<typeof TicketedEventCollectionPublicSchema>
